import { DineInQrStatus } from '../../../../../../../../Interfaces/componentTypes';
import { IDineInUnit } from '../../../../../../../../Interfaces/responseTypes';

export const isActiveDineInUnit = (unit: IDineInUnit): boolean => unit.quantity > 0;

export const getActiveDineInUnits = (units: IDineInUnit[]): IDineInUnit[] =>
    units.filter(isActiveDineInUnit);

export const isUnitQrGenerated = (unit: IDineInUnit): boolean =>
    unit.qr_data != null && unit.qr_data.length === unit.quantity;

export const getUnitQrCount = (unit: IDineInUnit): number => unit.qr_data?.length ?? 0;

export const getUnitQrStatus = (unit: IDineInUnit): DineInQrStatus => {
    const qrCount = getUnitQrCount(unit);

    if (qrCount === unit.quantity && qrCount > 0) {
        return DineInQrStatus.GENERATED;
    }

    if (qrCount > 0) {
        return DineInQrStatus.PARTIAL;
    }

    return DineInQrStatus.PENDING;
};

export interface IUnitQrStatusMeta {
    status: DineInQrStatus;
    titleKey: string;
    subtitleKey: string;
    countLabel?: string;
}

export const getUnitQrStatusMeta = (unit: IDineInUnit): IUnitQrStatusMeta => {
    const status = getUnitQrStatus(unit);
    const qrCount = getUnitQrCount(unit);

    if (status === DineInQrStatus.GENERATED) {
        return {
            status,
            titleKey: 'My_Business.My_Services.Dine_In.QR_Status_Generated',
            subtitleKey: 'My_Business.My_Services.Dine_In.QR_Status_Generated_Hint',
            countLabel: `${qrCount}/${unit.quantity}`,
        };
    }

    if (status === DineInQrStatus.PARTIAL) {
        return {
            status,
            titleKey: 'My_Business.My_Services.Dine_In.QR_Status_Partial',
            subtitleKey: 'My_Business.My_Services.Dine_In.QR_Status_Partial_Hint',
            countLabel: `${qrCount}/${unit.quantity}`,
        };
    }

    return {
        status,
        titleKey: 'My_Business.My_Services.Dine_In.QR_Status_Pending',
        subtitleKey: 'My_Business.My_Services.Dine_In.QR_Status_Pending_Hint',
        countLabel: `0/${unit.quantity}`,
    };
};

export const isUnitConfigChanged = (existing: IDineInUnit, updated: IDineInUnit): boolean =>
    existing.type !== updated.type || existing.quantity !== updated.quantity;

export const mergeEditedUnit = (existing: IDineInUnit, updated: IDineInUnit): IDineInUnit => {
    if (isUnitConfigChanged(existing, updated)) {
        return { ...updated, qr_data: null };
    }

    return { ...updated, qr_data: existing.qr_data ?? null };
};

export const createNewDineInUnit = (unit: IDineInUnit): IDineInUnit => ({
    ...unit,
    qr_data: null,
    extra_cost: null,
});

export const toDineInUnitPayload = (unit: IDineInUnit) => ({
    type: unit.type,
    quantity: unit.quantity,
    extra_cost: null,
});

export const getTotalDineInCapacity = (units: IDineInUnit[]): number =>
    getActiveDineInUnits(units).reduce((total, unit) => total + unit.quantity, 0);
